<template>
  <div class="tutorial">
    <!-- Шапка -->
    <div class="tutorial-hero">
      <div>
        <h1 class="tutorial-title">Обучение работе в AVR+</h1>
        <p class="tutorial-subtitle">
          Пошаговые инструкции по каждой роли, скриншоты экранов, видеоуроки и инструкции в PDF.
          Выберите роль — откроются разделы для неё; раздел «Общее» полезен всем.
        </p>
      </div>
      <div class="tutorial-search">
        <i class="material-icons">search</i>
        <input v-model.trim="search" type="text" placeholder="Поиск по инструкции: например, «NCALayer», «заголовок», «Excel»">
        <button v-if="search" type="button" title="Очистить" @click="search = ''">
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>

    <!-- Роли -->
    <div class="role-tabs">
      <button v-for="role in roles" :key="role.code" type="button" class="role-tab"
              :class="{active: activeRole === role.code, mine: myRoleCodes.includes(role.code)}"
              @click="selectRole(role.code)">
        <i class="material-icons">{{ role.icon }}</i>
        <span>{{ role.title }}</span>
        <small v-if="myRoleCodes.includes(role.code)">ваша роль</small>
      </button>
    </div>

    <div class="tutorial-body">
      <!-- Оглавление -->
      <aside class="tutorial-nav">
        <div class="nav-title">Разделы</div>
        <a v-for="section in visibleSections" :key="section.id" :href="'#' + section.id" class="nav-link"
           :class="{active: currentSection === section.id}" @click.prevent="scrollTo(section.id)">
          <span class="nav-badge" :class="'badge-' + section.role">{{ roleTitle(section.role) }}</span>
          {{ section.title }}
        </a>
        <div class="nav-title" style="margin-top: 1rem">Материалы</div>
        <a href="#videos" class="nav-link" @click.prevent="scrollTo('videos')">Видеоуроки</a>
        <a href="#pdfs" class="nav-link" @click.prevent="scrollTo('pdfs')">Инструкции PDF</a>
        <a href="#support" class="nav-link" @click.prevent="scrollTo('support')">Поддержка</a>
      </aside>

      <!-- Содержимое -->
      <main class="tutorial-content">
        <div v-if="!visibleSections.length" class="empty">
          По запросу «{{ search }}» ничего не найдено. Попробуйте другое слово или сбросьте поиск.
        </div>

        <section v-for="section in visibleSections" :key="section.id" :id="section.id" class="tutorial-section">
          <div class="section-head">
            <span class="nav-badge" :class="'badge-' + section.role">{{ roleTitle(section.role) }}</span>
            <h2>{{ section.title }}</h2>
          </div>
          <p v-if="section.intro" class="section-intro" v-html="section.intro"></p>

          <div v-for="(step, index) in section.steps" :key="index" class="step">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-body">
              <h3 class="step-title">{{ step.title }}</h3>
              <div class="step-text" v-html="highlight(step.text)"></div>

              <figure v-if="step.img && !brokenImages[step.img]" class="step-figure" @click="openImage(step)">
                <img :src="imageUrl(step.img)" :alt="step.title" loading="lazy" @error="imageFailed(step.img)">
                <figcaption><i class="material-icons">zoom_in</i> {{ step.title }} — нажмите, чтобы увеличить</figcaption>
              </figure>

              <div v-if="step.tips && step.tips.length" class="tips">
                <div class="tips-title"><i class="material-icons">lightbulb</i> Полезно знать</div>
                <ul>
                  <li v-for="(tip, i) in step.tips" :key="i" v-html="highlight(tip)"></li>
                </ul>
              </div>

              <table v-if="step.statuses" class="status-table">
                <thead>
                <tr>
                  <th>Статус</th>
                  <th>У кого акт</th>
                  <th>Что происходит</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="status in statuses" :key="status.name">
                  <td><span class="status-chip">{{ status.name }}</span></td>
                  <td>{{ status.who }}</td>
                  <td>{{ status.text }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- Видео -->
        <section id="videos" class="tutorial-section">
          <div class="section-head"><h2>Видеоуроки</h2></div>
          <p class="section-intro">Короткие записи экрана по основным сценариям. Видео можно развернуть на весь экран.</p>
          <div class="video-grid">
            <div v-for="video in videos" :key="video.file" class="video-card">
              <video :src="video.file" controls preload="metadata"></video>
              <div class="video-title">{{ video.title }}</div>
              <div class="video-text">{{ video.text }}</div>
            </div>
          </div>
        </section>

        <!-- PDF -->
        <section id="pdfs" class="tutorial-section">
          <div class="section-head"><h2>Инструкции в PDF</h2></div>
          <p class="section-intro">Полные инструкции по ролям для печати и рассылки коллегам.</p>
          <div class="pdf-list">
            <a v-for="pdf in pdfs" :key="pdf.file" :href="pdf.file" target="_blank" class="pdf-link"
               :class="{mine: pdf.roles.some(r => myRoleCodes.includes(r))}">
              <i class="material-icons">picture_as_pdf</i>
              <span>{{ pdf.title }}</span>
              <small v-if="pdf.roles.some(r => myRoleCodes.includes(r))">для вашей роли</small>
            </a>
          </div>
        </section>

        <!-- Поддержка -->
        <section id="support" class="tutorial-section">
          <div class="section-head"><h2>Поддержка</h2></div>
          <p class="section-intro">
            Не нашли ответа? Посмотрите раздел
            <router-link :to="{name: 'learning'}" class="custom-link">FAQ</router-link>
            или напишите нам через раздел
            <router-link :to="{name: 'offers'}" class="custom-link">«Предложения»</router-link>.
            По техническим вопросам:
            <a :href="support.phoneHref" class="custom-link">{{ support.phone }}</a>,
            <a :href="'mailto:' + support.email" class="custom-link">{{ support.email }}</a>.
          </p>
          <p class="section-intro">
            В обращении укажите вашу роль, номер акта или договора и приложите скриншот ошибки — так мы ответим быстрее.
          </p>
        </section>
      </main>
    </div>

    <!-- Просмотр скриншота -->
    <div v-if="lightbox" class="lightbox" @click="lightbox = null">
      <div class="lightbox-inner" @click.stop>
        <div class="lightbox-head">
          <span>{{ lightbox.title }}</span>
          <button type="button" @click="lightbox = null"><i class="material-icons">close</i></button>
        </div>
        <img :src="imageUrl(lightbox.img)" :alt="lightbox.title">
      </div>
    </div>
  </div>
</template>

<script>
import {ROLES, SECTIONS, VIDEOS, PDFS, SUPPORT, STATUSES} from '@/data/tutorial';

export default {
  name: 'Tutorial',
  data() {
    return {
      roles: ROLES,
      sections: SECTIONS,
      videos: VIDEOS,
      pdfs: PDFS,
      support: SUPPORT,
      statuses: STATUSES,
      activeRole: 'all',
      myRoleCodes: [],
      search: '',
      currentSection: '',
      brokenImages: {},
      lightbox: null,
    };
  },
  computed: {
    // разделы выбранной роли + «Общее»; при поиске — по всем ролям
    visibleSections() {
      let query = this.search.toLowerCase();
      return this.sections
          .filter(section => query !== '' || section.role === 'all' || section.role === this.activeRole)
          .map(section => {
            if (query === '') {
              return section;
            }
            let steps = section.steps.filter(step => this.stepText(step).toLowerCase().includes(query));
            if (section.title.toLowerCase().includes(query) || (section.intro || '').toLowerCase().includes(query)) {
              steps = section.steps;
            }
            return steps.length ? Object.assign({}, section, {steps: steps}) : null;
          })
          .filter(section => section !== null);
    },
  },
  mounted() {
    this.detectRoles();
    if (this.$route.hash) {
      this.$nextTick(() => this.scrollTo(this.$route.hash.replace('#', '')));
    }
    window.addEventListener('scroll', this.onScroll, {passive: true});
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    detectRoles() {
      let roleIds = [];
      try {
        roleIds = JSON.parse(localStorage.getItem('roles') || '[]');
      } catch (e) {
        roleIds = [];
      }
      if (!Array.isArray(roleIds)) {
        roleIds = String(roleIds).split(',').map(Number);
      }
      this.myRoleCodes = this.roles
          .filter(role => role.roleIds.some(id => roleIds.includes(id)))
          .map(role => role.code);
      this.activeRole = this.myRoleCodes[0] || 'all';
    },
    selectRole(code) {
      this.activeRole = code;
      this.search = '';
      this.$nextTick(() => window.scrollTo({top: 0, behavior: 'smooth'}));
    },
    roleTitle(code) {
      let role = this.roles.find(r => r.code === code);
      return role ? role.title : code;
    },
    imageUrl(file) {
      // папка не должна совпадать с роутом /tutorial
      return '/tutorial_img/' + file;
    },
    imageFailed(file) {
      this.$set(this.brokenImages, file, true);
    },
    openImage(step) {
      this.lightbox = {img: step.img, title: step.title};
    },
    stepText(step) {
      return [step.title, step.text].concat(step.tips || []).join(' ').replace(/<[^>]+>/g, ' ');
    },
    // подсветка найденного слова (текст уже содержит доверенный html из data/tutorial.js)
    highlight(html) {
      let query = this.search;
      if (!query || query.length < 2) {
        return html;
      }
      let escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return html.replace(new RegExp('(' + escaped + ')(?![^<]*>)', 'gi'), '<mark>$1</mark>');
    },
    scrollTo(id) {
      let element = document.getElementById(id);
      if (element) {
        window.scrollTo({top: element.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth'});
        this.currentSection = id;
      }
    },
    onScroll() {
      let ids = this.visibleSections.map(section => section.id).concat(['videos', 'pdfs', 'support']);
      let current = '';
      for (let i = 0; i < ids.length; i++) {
        let element = document.getElementById(ids[i]);
        if (element && element.getBoundingClientRect().top - 120 <= 0) {
          current = ids[i];
        }
      }
      this.currentSection = current;
    },
  },
};
</script>

<style scoped>
.tutorial {
  width: 92%;
  max-width: 1400px;
  margin: 2rem auto 4rem auto;
  color: #1f2937;
}

.tutorial-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.tutorial-title {
  font-size: 2rem;
  font-weight: 700;
  color: #23468c;
  margin: 0 0 0.5rem 0;
}

.tutorial-subtitle {
  max-width: 720px;
  color: #4b5563;
  line-height: 1.5;
}

.tutorial-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  min-width: 340px;
  flex: 1;
  max-width: 480px;
}

.tutorial-search input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.95rem;
}

.tutorial-search button {
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
}

.role-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.role-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all .15s ease-in-out;
}

.role-tab:hover {
  border-color: #23468c;
}

.role-tab.active {
  background: #23468c;
  color: white;
  border-color: #23468c;
}

.role-tab small {
  font-size: 0.7rem;
  opacity: 0.8;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: rgba(35, 70, 140, 0.12);
}

.role-tab.active small {
  background: rgba(255, 255, 255, 0.25);
}

.tutorial-body {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2rem;
  align-items: start;
}

.tutorial-nav {
  position: sticky;
  top: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  max-height: calc(100vh - 2rem);
  overflow: auto;
}

.nav-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.nav-link {
  display: block;
  padding: 0.45rem 0.5rem;
  border-radius: 8px;
  color: #1f2937;
  font-size: 0.9rem;
  line-height: 1.3;
  text-decoration: none;
}

.nav-link:hover {
  background: #f3f4f6;
}

.nav-link.active {
  background: #e8eefb;
  color: #23468c;
  font-weight: 600;
}

.nav-badge {
  display: inline-block;
  font-size: 0.65rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  margin-right: 0.35rem;
  vertical-align: middle;
  background: #e5e7eb;
  color: #374151;
  white-space: nowrap;
}

.badge-all { background: #e5e7eb; }
.badge-impl { background: #dbeafe; color: #1e40af; }
.badge-abp { background: #fef3c7; color: #92400e; }
.badge-appr { background: #dcfce7; color: #166534; }
.badge-book { background: #ede9fe; color: #5b21b6; }
.badge-dzo { background: #fee2e2; color: #991b1b; }

.tutorial-content {
  min-width: 0;
}

.empty {
  background: white;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.tutorial-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  scroll-margin-top: 90px;
}

.section-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.section-head h2 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.section-intro {
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.step {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 1rem;
  padding: 1rem 0;
  border-top: 1px solid #f3f4f6;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #23468c;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.step-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0.2rem 0 0.4rem 0;
}

.step-text {
  line-height: 1.6;
  color: #374151;
}

.step-text >>> ul {
  list-style: disc;
  padding-left: 1.4rem;
  margin: 0.5rem 0;
}

.step-text >>> li {
  margin: 0.25rem 0;
}

.step-text >>> mark, .tips >>> mark {
  background: #fde68a;
  padding: 0 0.1rem;
}

.step-figure {
  margin: 1rem 0 0.5rem 0;
  cursor: zoom-in;
}

.step-figure img {
  width: 100%;
  max-width: 900px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.step-figure figcaption {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.step-figure figcaption i {
  font-size: 1rem;
}

.tips {
  margin-top: 0.75rem;
  background: #f8fafc;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.tips-title {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
}

.tips-title i {
  color: #f59e0b;
  font-size: 1.1rem;
}

.tips ul {
  list-style: disc;
  padding-left: 1.4rem;
  font-size: 0.92rem;
  color: #374151;
}

.status-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.75rem;
  font-size: 0.9rem;
}

.status-table th, .status-table td {
  border: 1px solid #e5e7eb;
  padding: 0.5rem 0.6rem;
  text-align: left;
  vertical-align: top;
}

.status-table th {
  background: #f3f4f6;
}

.status-chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #e8eefb;
  color: #23468c;
  font-weight: 600;
  white-space: nowrap;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}

.video-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fafafa;
}

.video-card video {
  width: 100%;
  background: black;
  aspect-ratio: 16 / 9;
}

.video-title {
  font-weight: 600;
  padding: 0.6rem 0.8rem 0.2rem 0.8rem;
}

.video-text {
  font-size: 0.85rem;
  color: #6b7280;
  padding: 0 0.8rem 0.8rem 0.8rem;
}

.pdf-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pdf-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #1f2937;
  text-decoration: none;
}

.pdf-link:hover {
  border-color: #23468c;
  background: #f8fafc;
}

.pdf-link i {
  color: #dc2626;
}

.pdf-link.mine {
  border-color: #23468c;
}

.pdf-link small {
  margin-left: auto;
  font-size: 0.75rem;
  color: #23468c;
}

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.75);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.lightbox-inner {
  background: white;
  border-radius: 12px;
  max-width: 95vw;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.lightbox-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
}

.lightbox-head button {
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
}

.lightbox-inner img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

@media (max-width: 1000px) {
  .tutorial-body {
    grid-template-columns: 1fr;
  }

  .tutorial-nav {
    position: static;
    max-height: none;
  }

  .tutorial-section {
    padding: 1rem;
  }
}
</style>
